import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { MottuTranslateModule, TranslateLoader, translateLoaderFactory } from '@mottu-ops/locale';

import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { KeycloakAngularModule, KeycloakService } from '@mottu-ops/keycloak-angular';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { AppRoutingModule } from './app-routing.module';
import { MonitorDialog } from './shared/dialogs/monitor-dialog/monitor-dialog';
import { ResolverDialog } from './shared/dialogs/resolver-dialog/resolver-dialog';

import { BlockScrollStrategy, Overlay } from '@angular/cdk/overlay';
import { registerLocaleData } from '@angular/common';
import ptBr from '@angular/common/locales/pt';
import { LOCALE_ID } from '@angular/core';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { ServiceWorkerModule } from '@angular/service-worker';
import { MottuModalConfirmModule } from '@mottu-ops/global-components';
import { GANTT_GLOBAL_CONFIG, NgxGanttModule } from '@worktile/gantt';
import { NgxMaskModule } from "ngx-mask";
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AdminGuard } from './components/auth/admin.guard';
import { AuthGuard } from './components/auth/auth.guard';
import { HttpErrorInterceptor } from './components/auth/error.interceptor';
import { GlobalErrorHandler } from './components/auth/handler/global-error.handler';
import { MonitorProfileGuard } from './components/auth/monitor.profile.guard';
import { LoginLayoutComponent } from './components/layouts/login-layout.component';
import { MaterialModule } from './material.module';
import { NoteModalComponent } from './shared/components/note-modal/note-modal.component';
import { EmployeeGeoLimitsModalComponent } from './shared/components/employee-geo-limits-modal/employee-geo-limits-modal.component';
import { PdfViewerModule } from './shared/components/pdf-viewer/pdf-viewer.module';
import { QuizDynamicFormModule } from './shared/components/quiz-dynamic-form/quiz-dynamic-form.module';
import { AddComentarioDialog } from './shared/dialogs/add-comentario-dialog/add-comentario-dialog';
import { ChangeProtocolDialog } from './shared/dialogs/changeprotocol-dialog/changeprotocol-dialog';
import { ChecklistDialog } from './shared/dialogs/checklist-dialog/checklist-dialog';
import { ConfirmarDataDialog } from './shared/dialogs/confirmar-data-dialog/confirmar-data-dialog';
import { ConfirmarDialog } from './shared/dialogs/confirmar-dialog/confirmar-dialog';
import { DistribuicaoConfirmarDialog } from './shared/dialogs/distribuicao-confirmar-dialog/distribuicao-confirmar-dialog';
import { CrlvViewerDialog } from './shared/dialogs/crlv-viewer-dialog/crlv-viewer-dialog';
import { DeleteDataDialog } from './shared/dialogs/delete-data-dialog/delete-data-dialog';
import { DeleteDialog } from './shared/dialogs/delete-dialog/delete-dialog';
import { GaleryDialog } from './shared/dialogs/galery-dialog/galery-dialog';
import { GenerateCrrDialog } from './shared/dialogs/generate-crr-dialog/generate-crr-dialog';
import { ImageDialog } from './shared/dialogs/image-dialog/image-dialog';
import { InputDialog } from './shared/dialogs/input-dialog/input-dialog';
import { NotificarMovimentacaoDialog } from './shared/dialogs/notificar-movimentacao-dialog/notificar-movimentacao-dialog';
import { ReprocessarDialog } from './shared/dialogs/reprocessar-dialog/reprocessar-dialog';
import { CustomLoaderInterceptor } from './shared/interceptors/loader-interceptor';
import { AutoCompleteGoogleService } from './shared/services/autocomplete-google.service';
import { ChecklistService } from './shared/services/checklist.service';
import { InternalAuthService } from './shared/services/internalauth.service';
import { NewsletterService } from './shared/services/newsletter.service';
import { OneSignalService } from './shared/services/oneSignal.service';

registerLocaleData(ptBr);

export function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

export function scrollFactory(overlay: Overlay): () => BlockScrollStrategy {
  return () => overlay.scrollStrategies.block();
}

export function initializeKeycloak(keycloak: KeycloakService) {
  return () =>
    keycloak.init({
      config: {
        url: environment.keycloak.url,
        realm: environment.keycloak.realm,
        clientId: environment.keycloak.clientId,
      },
      initOptions: {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/assets/sso/silent-check-sso.html',
        silentCheckSsoFallback: !isSafari(),
        checkLoginIframe: !isSafari(),
      },
      enableBearerInterceptor: true,
    });
}

@NgModule({
  declarations: [
    AppComponent,
    LoginLayoutComponent,
    MonitorDialog,
    ImageDialog,
    ResolverDialog,
    DeleteDialog,
    DeleteDataDialog,
    ConfirmarDialog,
    ConfirmarDataDialog,
    DistribuicaoConfirmarDialog,
    InputDialog,
    ChecklistDialog,
    GaleryDialog,
    AddComentarioDialog,
    NotificarMovimentacaoDialog,
    GenerateCrrDialog,
    ReprocessarDialog,
    ChangeProtocolDialog,
    CrlvViewerDialog,
    NoteModalComponent,
    EmployeeGeoLimitsModalComponent,
  ],
  imports: [
    NgxGanttModule,
    BrowserModule,
    AppRoutingModule,
    InfiniteScrollModule,
    HttpClientModule,
    MaterialModule,
    BrowserAnimationsModule,
    FormsModule,
    QuizDynamicFormModule,
    ReactiveFormsModule,
    PdfViewerModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production && !isSafari(),
      registrationStrategy: 'registerWhenStable:5000',
    }),
    MottuModalConfirmModule,
    MottuTranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateLoaderFactory([
          {
            prefix: './assets/i18n/',
            suffix: '.json',
          },
        ]),
        deps: [HttpClient],
      },
      isolate: false,
      extend: true
    }),
    MatIconModule,
    KeycloakAngularModule,
    NgxMaskModule.forRoot()
  ],
  providers: [
    InternalAuthService,
    NewsletterService,
    AuthGuard,
    MonitorProfileGuard,
    AdminGuard,
    AutoCompleteGoogleService,
    ChecklistService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService],
    },
    OneSignalService,
    { provide: LOCALE_ID, useValue: 'pt-PT' },
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: JwtInterceptor,
    //   multi: true
    // },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: AuthInterceptor,
    //   multi: true,
    // },
    {
      provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
      useFactory: scrollFactory,
      deps: [Overlay],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomLoaderInterceptor,
      multi: true
    },
    {
      provide: GANTT_GLOBAL_CONFIG,
      useValue: {
        dateFormat: {
          day: 'dd/MM/yyyy',
          month: 'MM/yyyy',
          week: 'dd/MM/yyyy',
        }
      }
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
